import React, { useState, useEffect, useRef, memo } from 'react';

/**
 * LazyImage - Optimized image component with lazy loading and progressive enhancement
 * @param {string} src - Image source URL
 * @param {string} alt - Alt text for accessibility
 * @param {string} placeholder - Low-quality placeholder image (optional)
 * @param {string} className - Additional CSS classes
 * @param {Function} onLoad - Callback when image loads
 * @param {Function} onError - Callback when image fails to load
 */
const LazyImage = memo(function LazyImage({ 
  src, 
  alt, 
  placeholder = null,
  className = '',
  onLoad = null,
  onError = null,
  ...props 
}) {
  const [imageSrc, setImageSrc] = useState(placeholder || null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const imageRef = useRef(null);
  
  // Set up intersection observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before image enters viewport
      }
    );
    
    if (imageRef.current) {
      observer.observe(imageRef.current);
    }
    
    return () => {
      if (imageRef.current) {
        observer.unobserve(imageRef.current);
      }
    };
  }, []);
  
  // Load actual image when in view
  useEffect(() => {
    if (isInView && src) {
      const img = new Image();
      
      img.onload = () => {
        setImageSrc(src);
        setIsLoading(false);
        if (onLoad) onLoad();
      };
      
      img.onerror = () => {
        setIsLoading(false);
        if (onError) onError();
      };
      
      img.src = src;
    }
  }, [isInView, src, onLoad, onError]);
  
  return (
    <div 
      ref={imageRef}
      className={`relative overflow-hidden ${className}`}
    >
      {isLoading && (
        <div className="absolute inset-0 bg-base-200 animate-pulse" />
      )}
      
      {imageSrc && (
        <img
          src={imageSrc}
          alt={alt}
          className={`
            transition-opacity duration-300
            ${isLoading ? 'opacity-0' : 'opacity-100'}
            ${className}
          `}
          {...props}
        />
      )}
      
      {!imageSrc && !isLoading && (
        <div className="absolute inset-0 bg-base-200 flex items-center justify-center">
          <span className="text-base-content/50">Failed to load image</span>
        </div>
      )}
    </div>
  );
});

LazyImage.displayName = 'LazyImage';

export default LazyImage;